import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import validator from "validator";

import { gql } from "@examspark/api";
import { assertNativeError, getTagFromResource } from "@examspark/common";
import { useAppContext } from "@examspark/common-app";
import {
  Callout,
  Form,
  Modal,
  ModalProps,
  SubmitButtons,
  TextInput,
  VSpace,
} from "@examspark/common-ui";
import { IconInformationCircleLine } from "@examspark/common-ui/dist/components/icons";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { ModelWorkspaceConnection } from "@examspark/types";

const WorkspaceDomainsModal = (
  props: ModalProps & {
    onSave?: () => void;
  },
) => {
  const [context] = useAppContext();
  const workspaceId = context.workspaceUser?.id;
  const [domain, setDomain] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ["registration-domains", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return;
      const result = (await gql.operations.registration.listRegistrationDomains(
        workspaceId,
      )) as ModelWorkspaceConnection;
      console.log(result);

      return result;
    },
  });

  const _domain = data?.items?.find(
    (r) => getTagFromResource(r, "type")?.value === "primary",
  );

  useEffect(() => {
    setDomain(_domain?.id ?? "");
  }, [_domain?.id]);

  const handleSubmit = async () => {
    try {
      if (domain.trim().length && !validator.isEmail(`foo@${domain}`))
        throw new Error("Invalid registration domain");

      setIsSaving(true);
      if (!workspaceId) return;
      if (_domain != null) {
        await gql.operations.registration.deleteRegistrationDomain(
          workspaceId,
          _domain.id,
        );
      }

      if (domain.trim().length) {
        await gql.operations.registration.createRegistrationDomain(
          workspaceId,
          domain,
          [
            {
              key: "type",
              value: "primary",
              type: "string",
            },
          ],
        );
      }

      await refetch();
      setIsSaving(false);
      props.onSave?.();
    } catch (error) {
      setIsSaving(false);
      assertNativeError(error);
      alert(error.message);
    }
  };

  useEffect(() => {
    if (props.isOpen) refetch();
  }, [props.isOpen, refetch]);

  return (
    <Modal
      title="Registration Settings"
      size="small"
      buttons={
        <SubmitButtons
          submitLabel="Save Domains"
          isSubmitting={isSaving}
          onCancel={props.onRequestClose}
          onSubmit={() => handleSubmit()}
        />
      }
      {...props}
    >
      <PaddedBody>
        <VSpace size="2rem">
          <Callout icon={<IconInformationCircleLine width="16" />}>
            Restrict access to your workspace by setting up an email domain.
            Users will only be able to sign up using email addresses whose
            domains match the one below.
          </Callout>
          <VSpace size="1.5rem">
            <Form.Field label="Registration Domain">
              <TextInput
                placeholder="ex: mydistrict.edu"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onEnterKeyPress={() => handleSubmit()}
                disabled={isSaving}
              />
            </Form.Field>
          </VSpace>
        </VSpace>
      </PaddedBody>
    </Modal>
  );
};

export default WorkspaceDomainsModal;
